import { useState, useCallback, useRef, useEffect } from 'react';
import { Card, List, Tag, Typography, message, Button, Space, Spin, Alert } from 'antd';
import { CheckCircleOutlined, QuestionCircleOutlined, LoadingOutlined, CameraOutlined } from '@ant-design/icons';
import DataMatrixScanner, { type DataMatrixScannerHandle } from '../../components/marking/DataMatrixScanner';
import GtinBindModal from '../../components/marking/GtinBindModal';
import { markingApi, type MarkingCode, type GtinInfo } from '../../api/marking';
import useScanBufferStore from '../../store/scanBufferStore';
import useIsMobile from '../../hooks/useIsMobile';
import useBottomBarStore from '../../layout/mobileBottomBarSlotStore';

const { Text } = Typography;

interface PendingScan {
  raw_code: string;
  gtin: string | null;
  serial_number: string | null;
  gtinInfo: GtinInfo | null;
}

const MarkingScanPage: React.FC = () => {
  const isMobile = useIsMobile();
  const scannerRef = useRef<DataMatrixScannerHandle>(null);
  const setCenter = useBottomBarStore((s) => s.setCenter);
  const [pending, setPending] = useState<PendingScan[]>([]);
  const [saved, setSaved] = useState<MarkingCode[]>([]);
  const [checking, setChecking] = useState<{ gtin: string | null } | null>(null);
  const [bindModal, setBindModal] = useState<{ open: boolean; gtin: string; index: number }>({
    open: false, gtin: '', index: -1,
  });
  // Тосты «уже отсканирован» — один раз на код, чтобы не спамить при каждом
  // повторном срабатывании сканера (cooldown 2.5с → ~24 события/мин).
  const warnedRef = useRef<Set<string>>(new Set());

  // Регистрируем центральную кнопку bottom bar на мобиле — рестарт камеры.
  // При размонтировании чистим слот, иначе кнопка осталась бы на других страницах.
  useEffect(() => {
    if (!isMobile) return;
    setCenter(
      <Button
        type="primary"
        shape="circle"
        size="large"
        icon={<CameraOutlined />}
        onClick={() => scannerRef.current?.restart()}
        style={{ width: 52, height: 52, transform: 'translateY(-6px)' }}
        aria-label="Рестарт камеры"
      />,
    );
    return () => setCenter(null);
  }, [isMobile, setCenter]);

  const handleScan = useCallback(async (raw: string) => {
    // Проверка по marking_code (конкретная бутылка уже отсканирована)
    // raw_code может отличаться (с криптохвостом / без), но marking_code одинаковый
    const dupPending = pending.find(p => p.gtin && raw.includes(p.gtin));
    if (dupPending) {
      if (!warnedRef.current.has(raw)) {
        warnedRef.current.add(raw);
        const label = dupPending?.gtinInfo
          ? [dupPending.gtinInfo.brand, dupPending.gtinInfo.product_name, dupPending.gtinInfo.container_size].filter(Boolean).join(' · ')
          : '';
        message.warning(
          label
            ? `Уже отсканирован: ${label}`
            : `Уже отсканирован${dupPending.gtin ? ` (GTIN ${dupPending.gtin})` : ''}`,
          3,
        );
      }
      return;
    }

    // Извлекаем GTIN локально для превью — покажем пользователю, что проверяем
    const gtinMatch = raw.match(/^01(\d{14})/);
    const previewGtin = gtinMatch ? gtinMatch[1] : null;
    setChecking({ gtin: previewGtin });

    try {
      const { data: scan } = await markingApi.createScan(raw);
      useScanBufferStore.getState().refreshItems();

      if (scan.already_in_user_buffer) {
        if (scan.status === 'reserved' && scan.order_number) {
          message.warning(`Марка уже в буфере. Эта бутылка уже в заказе № ${scan.order_number} — обновлено время сканирования`);
        } else if (scan.status === 'sold') {
          message.warning('Марка уже в буфере. Эта марка уже продана — обновлено время сканирования');
        } else {
          message.warning('Марка уже в буфере — обновлено время сканирования');
        }
      } else if (scan.status === 'reserved' && scan.order_number) {
        message.warning(`Эта бутылка уже в заказе № ${scan.order_number}`);
      } else if (scan.status === 'sold') {
        message.warning('Эта марка уже продана');
      } else if (scan.status === 'off_balance') {
        message.warning('Марка не найдена в справочнике 1С (вне баланса)');
      }

      // Полная привязка = товар + тара. Без тары отправляем в очередь привязки
      // (это legacy-сканы, где GTIN раньше добавлялся в product_catalog.gtin[] без size_id).
      const fullyBound = scan.product_name && scan.size_id;

      if (fullyBound) {
        message.success(`${scan.brand || ''} ${scan.product_name} · ${scan.container_size} — сохранено`);
        setSaved(prev => {
          const filtered = prev.filter(s => !(s.marking_code && scan.marking_code && s.marking_code === scan.marking_code));
          return [scan, ...filtered];
        });
      } else if (scan.gtin) {
        const { data: info } = await markingApi.getGtinInfo(scan.gtin);
        if (info.catalog_id && info.size_id) {
          message.success('Сохранено');
          setSaved(prev => {
            const filtered = prev.filter(s => !(s.marking_code && scan.marking_code && s.marking_code === scan.marking_code));
            return [scan, ...filtered];
          });
        } else {
          setPending(prev => [{ raw_code: raw, gtin: scan.gtin, serial_number: scan.serial_number, gtinInfo: info }, ...prev]);
          message.info(info.catalog_id ? 'Нужно указать тару для GTIN' : 'Новый GTIN — привяжите к товару');
        }
      } else {
        message.warning('Не удалось распознать GTIN из кода');
        setSaved(prev => {
          const filtered = prev.filter(s => !(s.marking_code && scan.marking_code && s.marking_code === scan.marking_code));
          return [scan, ...filtered];
        });
      }
    } catch (e: any) {
      const detail = e?.response?.data?.detail;
      if (e?.response?.status === 409) {
        message.warning('Эта бутылка уже отсканирована');
      } else if (detail === 'Этот код уже отсканирован') {
        message.warning('Этот код уже есть в базе');
      } else {
        message.error(detail || 'Ошибка сохранения');
      }
    } finally {
      setChecking(null);
    }
  }, [pending, saved]);

  const handleBind = (index: number, gtin: string) => {
    setBindModal({ open: true, gtin, index });
  };

  const handleBound = (_catalogId: number) => {
    const item = pending[bindModal.index];
    setBindModal({ open: false, gtin: '', index: -1 });
    if (item) {
      setPending(prev => prev.filter((_, i) => i !== bindModal.index));
      markingApi.listScans(0, 1).then(({ data }) => {
        if (data.length) {
          setSaved(prev => {
            const scan = data[0];
            const filtered = prev.filter(s => !(s.marking_code && scan.marking_code && s.marking_code === scan.marking_code));
            return [scan, ...filtered];
          });
        }
      });
    }
  };

  // Блок «Требуют привязки» — на мобиле без правых actions, кнопка «Привязать»
  // большая на всю ширину ниже meta — палец попадает в неё гарантированно.
  const renderPending = (item: PendingScan, idx: number) => (
    <List.Item
      style={isMobile ? { flexDirection: 'column', alignItems: 'stretch' } : undefined}
      actions={isMobile ? undefined : [
        <Button size="small" type="link" onClick={() => handleBind(idx, item.gtin!)}>
          Привязать
        </Button>,
      ]}
    >
      <List.Item.Meta
        avatar={<QuestionCircleOutlined style={{ color: '#faad14', fontSize: 20 }} />}
        title={
          item.gtinInfo?.product_name
            ? `${item.gtinInfo.brand || ''} ${item.gtinInfo.product_name}`.trim()
            : `GTIN: ${item.gtin}`
        }
        description={
          item.gtinInfo?.product_name
            ? `Нужно указать тару · GTIN: ${item.gtin}`
            : `Серийник: ${item.serial_number || '—'}`
        }
      />
      {isMobile && (
        <Button
          type="primary"
          size="large"
          block
          onClick={() => handleBind(idx, item.gtin!)}
          style={{ marginTop: 8 }}
        >
          Привязать
        </Button>
      )}
    </List.Item>
  );

  const scrollList = (
    <>
      {checking && (
        <Alert
          style={{ marginBottom: 12 }}
          type="info"
          showIcon
          icon={<Spin indicator={<LoadingOutlined style={{ fontSize: 22, color: '#1677ff' }} spin />} />}
          message={
            <Space direction="vertical" size={0}>
              <Typography.Text strong>Проверка товара в Честном Знаке…</Typography.Text>
              {checking.gtin && (
                <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                  GTIN: {checking.gtin}
                </Typography.Text>
              )}
            </Space>
          }
        />
      )}

      {pending.length > 0 && (
        <Card title="Требуют привязки" size="small" style={{ marginBottom: 12 }}>
          <List dataSource={pending} renderItem={renderPending} />
        </Card>
      )}

      {saved.length > 0 && (
        <Card title={`Сохранено в этой сессии (${saved.length})`} size="small">
          <List
            dataSource={saved}
            renderItem={(scan) => (
              <List.Item>
                <List.Item.Meta
                  avatar={<CheckCircleOutlined style={{ color: '#52c41a', fontSize: 20 }} />}
                  title={
                    <Space>
                      <span>{scan.product_name ? `${scan.brand || ''} ${scan.product_name}`.trim() : `GTIN: ${scan.gtin || '—'}`}</span>
                      {scan.container_size && <Tag color="blue">{scan.container_size}</Tag>}
                    </Space>
                  }
                  description={<Text type="secondary">{scan.serial_number || '—'}</Text>}
                />
              </List.Item>
            )}
          />
        </Card>
      )}
    </>
  );

  const modal = (
    <GtinBindModal
      open={bindModal.open}
      gtin={bindModal.gtin}
      onBound={handleBound}
      onCancel={() => setBindModal({ open: false, gtin: '', index: -1 })}
    />
  );

  // Мобильная раскладка: камера на верхние 50% экрана, нижняя половина —
  // scrollable список pending/saved. Компенсация -12px/-8px по margin
  // восполняет Content-отступ в Layout.tsx и растягивает экран на всю ширину.
  if (isMobile) {
    return (
      <div
        style={{
          height: 'calc(100dvh - 56px - env(safe-area-inset-bottom, 0px) - 24px)',
          display: 'flex',
          flexDirection: 'column',
          margin: -8,
        }}
      >
        <div style={{ flex: '0 0 auto', padding: 8, borderBottom: '1px solid #f0f0f0' }}>
          <DataMatrixScanner ref={scannerRef} onScan={handleScan} />
        </div>
        <div style={{ flex: 1, overflowY: 'auto', padding: 8 }}>{scrollList}</div>
        {modal}
      </div>
    );
  }

  return (
    <div style={{ maxWidth: 600, margin: '0 auto' }}>
      <Card title="Сканирование Data Matrix" style={{ marginBottom: 16 }}>
        <DataMatrixScanner ref={scannerRef} onScan={handleScan} />
      </Card>
      {scrollList}
      {modal}
    </div>
  );
};

export default MarkingScanPage;
